import {
  AggregateSessionScope,
  AttendanceCode,
  DEFAULT_DATE_FORMAT_FNS,
  useGetAttendanceCodesQuery,
  useGetGroupAttendanceAggregateQuery,
} from '@schooly/api';
import { useAuth } from '@schooly/components/authentication';
import { format } from 'date-fns';
import { useEffect, useMemo } from 'react';

import { toStudentRows } from '../common/attendanceGridData';
import {
  buildGroupGridFromAggregate,
  buildGroupSkeleton,
  GroupGridMember,
} from '../common/buildGroupGrid';
import { AttendanceGridColumn, AttendanceStudentRow } from '../common/types';

type UseAttendanceGridForGroupResult = {
  columns: AttendanceGridColumn[];
  rows: AttendanceStudentRow[];
  codes: AttendanceCode[];
  isTableLoading: boolean;
};

export const useAttendanceGridForGroup = (
  groupId: string,
  weekDates: string[],
  members: GroupGridMember[],
): UseAttendanceGridForGroupResult => {
  const { schoolId = '' } = useAuth();

  const weekStart = weekDates[0] ?? '';
  const weekEnd = weekDates[weekDates.length - 1] ?? '';
  const enabled = !!groupId && !!schoolId && !!weekStart && !!weekEnd;

  const { data: codes = [] } = useGetAttendanceCodesQuery(schoolId, { enabled: !!schoolId });

  const {
    data: aggregate,
    isFetching: isFetchingAggregate,
    setParams,
  } = useGetGroupAttendanceAggregateQuery(
    {
      groupId,
      school_id: schoolId,
      date_from: weekStart,
      date_to: weekEnd,
      session_scope: AggregateSessionScope.All,
    },
    { enabled, refetchOnMount: 'always' },
  );

  useEffect(() => {
    if (enabled) {
      setParams((p) => ({
        ...p,
        groupId,
        school_id: schoolId,
        date_from: weekStart,
        date_to: weekEnd,
      }));
    }
  }, [groupId, schoolId, weekStart, weekEnd, enabled, setParams]);

  const { columns, rows } = useMemo(() => {
    if (!weekDates.length || !members.length) return { columns: [], rows: [] };

    if (!aggregate) {
      const skeleton = buildGroupSkeleton(members, weekDates);
      return { columns: skeleton.columns, rows: toStudentRows(skeleton.rows) };
    }

    const grid = buildGroupGridFromAggregate({
      weekDates,
      today: format(new Date(), DEFAULT_DATE_FORMAT_FNS),
      members,
      aggregate,
    });
    return { columns: grid.columns, rows: toStudentRows(grid.rows) };
  }, [weekDates, members, aggregate]);

  return {
    columns,
    rows,
    codes,
    isTableLoading: isFetchingAggregate,
  };
};
